import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashboardNarrativePanel } from "src/components/common";
import { useMockAiSummaryUsage } from "src/hooks/useMockAiSummaryUsage";
import { formatNumber, isArabicLanguage } from "src/utils/numberFormat";
import { generateSocialReportExecutiveSummary } from "./socialReportAiSummary.data";
import { SOCIAL_PLATFORM_LABEL_KEY } from "./socialReportConfig";
import type { OrganicSocialPlatform, SocialReportBundle } from "../../../data/organicSocialMockData";

interface SocialReportAiSummaryProps {
  platform: OrganicSocialPlatform;
  report: SocialReportBundle;
  startDate: string;
  endDate: string;
}

const AI_SUMMARY_STORAGE_KEY_PREFIX = "organic_media_social_ai_summary_enabled_";

const readStoredSummaryEnabled = (platform: OrganicSocialPlatform): boolean => {
  try {
    return localStorage.getItem(`${AI_SUMMARY_STORAGE_KEY_PREFIX}${platform}`) !== "false";
  } catch {
    return true;
  }
};

const writeStoredSummaryEnabled = (platform: OrganicSocialPlatform, enabled: boolean) => {
  try {
    localStorage.setItem(`${AI_SUMMARY_STORAGE_KEY_PREFIX}${platform}`, String(enabled));
  } catch {
    // Storage can be unavailable (private mode, blocked site data) — the toggle
    // still works for the session, it just won't persist across reloads.
  }
};

const SocialReportAiSummary: FC<SocialReportAiSummaryProps> = ({
  platform,
  report,
  startDate,
  endDate,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = isArabicLanguage(i18n.language);
  const platformLabel = t(SOCIAL_PLATFORM_LABEL_KEY[platform]);
  const [analyzing, setAnalyzing] = useState(false);
  const [summaryEnabled, setSummaryEnabled] = useState(() => readStoredSummaryEnabled(platform));

  const handleSummaryEnabledChange = useCallback(
    (enabled: boolean) => {
      setSummaryEnabled(enabled);
      writeStoredSummaryEnabled(platform, enabled);
    },
    [platform],
  );

  const filterKey = `${platform}|${startDate}|${endDate}`;

  useEffect(() => {
    if (!summaryEnabled) {
      setAnalyzing(false);
      return;
    }
    setAnalyzing(true);
    const id = window.setTimeout(() => setAnalyzing(false), 420);
    return () => window.clearTimeout(id);
  }, [filterKey, summaryEnabled]);

  const fmt = useMemo(
    () => ({
      formatCount: (value: number) => formatNumber(value, {}, i18n.language),
      formatPercent: (value: number | null) =>
        value === null ? "—" : `${formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
      isRTL,
    }),
    [i18n.language, isRTL],
  );

  const summaryUsage = useMockAiSummaryUsage(`organic_social_${platform}`, filterKey, summaryEnabled);

  const summary = generateSocialReportExecutiveSummary({
    platform,
    platformLabel,
    report,
    startDate,
    endDate,
    fmt,
    t,
  });

  return (
    <div className="organic-social-ai-summary">
      <DashboardNarrativePanel
        narrative={summary.hasData ? summary : null}
        loading={analyzing}
        defaultCollapsed
        title={t("organicMedia.social.aiSummary.title", { defaultValue: "AI Executive Summary" })}
        subtitle={
          summary.periodLabel
            ? t("organicMedia.social.aiSummary.subtitle", {
                defaultValue: "Powered by DataTram AI · {{period}}",
                period: summary.periodLabel,
              })
            : undefined
        }
        aiSummary={{
          enabled: summaryEnabled,
          featureAvailable: true,
          setEnabled: handleSummaryEnabledChange,
          usage: summaryUsage,
        }}
      />
    </div>
  );
};

export default SocialReportAiSummary;
